/**
 * @file     client.ts
 * @readme   ./CLAUDE.md
 * @purpose  Browser-side Relta analytics SDK. Buffers events and flushes on:
 *           10-event batch, 5s idle, visibilitychange:hidden (sendBeacon),
 *           beforeunload (sendBeacon). Manages anon_id cookie + session_id
 *           sessionStorage. Respects navigator.doNotTrack.
 *
 *           Used by useAnalytics() (additive: GA4 calls are unchanged).
 *           Not for server-side use.
 */

'use client';

import type { WireEvent } from './types';
import { env } from '@/core/config/env';

const ANON_COOKIE = 'relta_aid';
// Session state lives in a cookie, not sessionStorage: sessionStorage is
// per-tab and dies on tab close, so two tabs were two sessions and the 30-min
// idle rule could never actually apply. Half of all recorded sessions held a
// single event as a result.
const SESSION_COOKIE = 'relta_sid';
const SESSION_IDLE_MS = 30 * 60 * 1000; // 30 min inactivity → new session
const FLUSH_INTERVAL_MS = 5000;
const FLUSH_BATCH_SIZE = 10;
const MAX_BUFFER = 50;
const INGEST_PATH = '/api/analytics/ingest';

let buffer: WireEvent[] = [];
let idleTimer: ReturnType<typeof setTimeout> | null = null;
let initialized = false;

function uuid(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  // Fallback for older browsers (rare in 2026): RFC4122-ish v4.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}

function dntActive(): boolean {
  if (!isBrowser()) return false;
  // Respect classic DNT plus newer GPC (Global Privacy Control) hint.
  const dnt = (navigator as Navigator & { doNotTrack?: string; globalPrivacyControl?: boolean }).doNotTrack;
  if (dnt === '1' || dnt === 'yes') return true;
  if ((navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true) return true;
  return false;
}

function readCookie(name: string): string | null {
  if (!isBrowser()) return null;
  const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
}

function writeCookie(name: string, value: string, maxAgeSec: number): void {
  if (!isBrowser()) return;
  const secure = location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAgeSec}; SameSite=Lax${secure}`;
}

function getAnonId(): string {
  if (!isBrowser()) return '';
  let id = readCookie(ANON_COOKIE);
  if (!id) {
    id = uuid();
    writeCookie(ANON_COOKIE, id, 60 * 60 * 24 * 365 * 2); // 2 years
  }
  return id;
}

interface SessionState {
  /** Session id. */
  i: string;
  /** Started at, epoch ms. Carried so session.end can report a duration. */
  s: number;
  /** Last seen, epoch ms. Drives the idle window. */
  l: number;
}

function readSessionState(): SessionState | null {
  const raw = readCookie(SESSION_COOKIE);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<SessionState>;
    if (typeof parsed.i !== 'string' || typeof parsed.s !== 'number' || typeof parsed.l !== 'number') {
      return null;
    }
    return parsed as SessionState;
  } catch {
    return null;
  }
}

function writeSessionState(state: SessionState): void {
  writeCookie(SESSION_COOKIE, JSON.stringify(state), Math.ceil(SESSION_IDLE_MS / 1000));
}

/**
 * Return the live session id, opening a new session when there is none or the
 * idle window has lapsed. A fresh session emits `session.start` immediately so
 * the session row records where the visit actually landed.
 */
function ensureSession(): SessionState {
  const now = Date.now();
  const existing = readSessionState();
  if (existing && now - existing.l <= SESSION_IDLE_MS) {
    const touched = { ...existing, l: now };
    writeSessionState(touched);
    return touched;
  }
  const fresh: SessionState = { i: uuid(), s: now, l: now };
  writeSessionState(fresh);
  // Queued against the new id directly, never back through ensureSession,
  // which would recurse.
  queueEvent('session.start', { entry_url: location.href }, fresh.i);
  return fresh;
}

function getSessionId(): string {
  if (!isBrowser()) return '';
  return ensureSession().i;
}

/** Buffer an event against an already-resolved session id. */
function queueEvent(eventName: string, payload: Record<string, unknown>, sessionId: string) {
  const candidate: WireEvent = {
    event_name: eventName,
    envelope: {
      url: location.href,
      referrer: document.referrer || undefined,
      session_id: sessionId,
      ts_client: new Date().toISOString(),
    },
    payload: payload ?? {},
  };
  // The browser constructs this envelope itself. The ingest route remains the
  // authoritative Zod validation boundary for all untrusted wire payloads;
  // avoiding the full Zod runtime here keeps analytics off the critical path.
  buffer.push(candidate);
  if (buffer.length >= MAX_BUFFER) {
    void flush('overflow');
    return;
  }
  if (buffer.length >= FLUSH_BATCH_SIZE) {
    void flush('batch');
    return;
  }
  scheduleIdleFlush();
}

function pushEvent(eventName: string, payload: Record<string, unknown>) {
  if (!isBrowser() || dntActive() || !env.enableAnalytics) return;
  // Middleware no longer mints relta_aid on a plain first GET (that kept pages
  // CDN-cacheable), so mint it on the first tracked event rather than the
  // first flush: a lead POST sent before the flush must still carry it.
  getAnonId();
  queueEvent(eventName, payload, ensureSession().i);
}

/**
 * Close the session out on the way off the page. Emitted every time the page
 * is hidden rather than once, because there is no reliable last-page signal on
 * mobile; ingest keeps the longest duration it has seen for the session.
 */
function endSession() {
  if (!isBrowser() || dntActive() || !env.enableAnalytics) return;
  const state = readSessionState();
  if (!state) return;
  queueEvent('session.end', { duration_ms: Math.max(0, Date.now() - state.s) }, state.i);
}

function scheduleIdleFlush() {
  if (idleTimer) clearTimeout(idleTimer);
  idleTimer = setTimeout(() => { void flush('idle'); }, FLUSH_INTERVAL_MS);
}

function snapshotAndClear(): WireEvent[] {
  if (buffer.length === 0) return [];
  const out = buffer;
  buffer = [];
  if (idleTimer) { clearTimeout(idleTimer); idleTimer = null; }
  return out;
}

async function flush(reason: 'batch' | 'idle' | 'unload' | 'visibility' | 'overflow') {
  if (!isBrowser()) return;
  const events = snapshotAndClear();
  if (events.length === 0) return;
  const body = JSON.stringify({ anon_id: getAnonId(), events });
  const useBeacon = (reason === 'unload' || reason === 'visibility') &&
    typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function';
  if (useBeacon) {
    try {
      navigator.sendBeacon(INGEST_PATH, new Blob([body], { type: 'application/json' }));
      return;
    } catch {
      // Fall through to fetch.
    }
  }
  try {
    await fetch(INGEST_PATH, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
      credentials: 'same-origin',
    });
  } catch {
    // Drop on failure. Analytics must not retry-storm or surface errors.
  }
}

function attachLifecycle() {
  if (!isBrowser() || initialized) return;
  initialized = true;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      endSession();
      void flush('visibility');
    }
  });
  window.addEventListener('pagehide', () => { endSession(); void flush('unload'); });
  window.addEventListener('beforeunload', () => { endSession(); void flush('unload'); });
}

// Admin workspace pages embed consumer components (map, listing sheets) whose
// events would otherwise land in analytics_events as product behavior. Agent
// activity is audited via logAgentAction/trackServerEvent server-side instead.
function onAdminSurface(): boolean {
  return isBrowser() && window.location.pathname.startsWith('/admin');
}

export const reltaAnalytics = {
  track(eventName: string, payload: Record<string, unknown> = {}) {
    if (onAdminSurface()) return;
    attachLifecycle();
    pushEvent(eventName, payload);
  },
  flushNow() {
    return flush('batch');
  },
  anonId() {
    return isBrowser() ? getAnonId() : '';
  },
  sessionId() {
    return isBrowser() ? getSessionId() : '';
  },
};
